import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { PivotCellSx } from "../../../dashboard-framework/pivot";
import { DateFilterKey, GlobalFilterConfig } from "../../../dashboard-framework/types";
import { REPORTS_FIRST_SERVICE, VIRTUAL_CLINIC_ID } from "../../../utils/constants";
import { COARSE_DISORDER_GROUPS, ClientTypeKey, CoarseDisorderGroupKey } from "../shared/disorderGroups";
import { CLINIC_OPTIONS } from "../shared/sharedMetrics";
import { ConversionRateBand, conversionRateBandOf } from "./clientConversionRows";

export const CLIENT_CONVERSION_KEY = "client-conversion-rate";

// Consulta de Avaliação (service 37) — a partir dela mede-se a conversão em cliente.
export const CONVERSION_SERVICE = REPORTS_FIRST_SERVICE;

// Campos enumerados (sem wildcards): identificação da pessoa (técnico→user), do tratamento (início,
// estado, diagnóstico) e do cliente (nº, data de nascimento para o split Adulto/Criança).
export const CONVERSION_SESSION_FIELDS = [
    "id",
    "start_date",
    "technician.id",
    "technician.user_id.id",
    "technician.user_id.first_name",
    "technician.user_id.last_name",
    "treatment.id",
    "treatment.date_of_start",
    "treatment.treatment_status_new",
    "treatment.diagnose_main.id",
    "treatment.diagnose_main.name",
    "treatment.client.id",
    "treatment.client.client_id",
    "treatment.client.birthday",
].join(",");

// Universos acima disto (8 páginas × 1000) disparam o aviso de registos truncados. All-time ≈ 3793.
export const CONVERSION_MAX_PAGES = 8;

// Subset de períodos: inclui "sem_periodo" (reproduz o all-time do Zoho). NUNCA incluir "personalizado"
// (o componente acrescenta-o sempre) e incluir SEMPRE o defaultPreset.
export const CONVERSION_PERIOD_PRESETS: Array<DateFilterKey> = ["este_mes", "ultimos_3_meses", "este_ano", "ultimos_12_meses", "sem_periodo"];
export const CONVERSION_DEFAULT_PRESET: DateFilterKey = "ultimos_12_meses";

const PERTURBACAO_ALL = "all";
const CLIENT_TYPE_ALL = "all";

export const CONVERSION_CLINIC_OPTIONS = CLINIC_OPTIONS.filter((option) => option.value !== VIRTUAL_CLINIC_ID);

export const conversionGlobalFilters: Array<GlobalFilterConfig> = [
    { key: "period", type: "datePreset", label: "Período", field: "start_date", defaultPreset: CONVERSION_DEFAULT_PRESET, presets: CONVERSION_PERIOD_PRESETS },
    { key: "clinic", type: "select", label: "Clínica", field: "clinic_id", options: CONVERSION_CLINIC_OPTIONS },
    {
        key: "disorderGroup",
        type: "choice",
        label: "Perturbação",
        defaultValue: PERTURBACAO_ALL,
        options: [{ value: PERTURBACAO_ALL, label: "Todas" }, ...COARSE_DISORDER_GROUPS.map((group) => ({ value: group.key, label: group.label }))],
    },
    {
        key: "clientType",
        type: "choice",
        label: "Tipo de cliente",
        defaultValue: CLIENT_TYPE_ALL,
        options: [
            { value: CLIENT_TYPE_ALL, label: "Todos" },
            { value: "adults", label: "Adulto" },
            { value: "children", label: "Criança" },
        ],
    },
];

export const conversionGroupKeyFromFilter = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === PERTURBACAO_ALL) return null;
    return String(value) as CoarseDisorderGroupKey;
};

export const conversionClinicLabelOf = (clinicId: string | number | null | undefined) => {
    const clinicLabel = CONVERSION_CLINIC_OPTIONS.find((option) => option.value === clinicId)?.label ?? null;
    return clinicLabel;
};

export const conversionClientTypeFromFilter =(value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === CLIENT_TYPE_ALL) return null;
    return String(value) as ClientTypeKey;
};

// Vista individual: filtra as consultas ao próprio técnico (psicóloga). Spelling aninhado por `.id` —
// o único validado sob token de psicóloga (espelha psychologistViewerFilter do teamKpisConfig).
export const conversionOwnViewFilter = (viewerUserId: string) => {
    const filter = { technician: { user_id: { id: { _eq: viewerUserId } } } };
    return filter;
};

// Semáforo do Zoho nas colunas de %: <30% vermelho · 30–35% verde claro · >35% verde escuro.
const BAND_BACKGROUND: Record<ConversionRateBand, string> = {
    low: DASHBOARD_COLORS.cancelled,
    mid: DASHBOARD_COLORS.done,
    high: DASHBOARD_COLORS.doneDark,
};

export const conversionRateCellSx: PivotCellSx = (rawValue) => {
    const band = conversionRateBandOf(rawValue);
    if (!band) return undefined;
    const sx = { backgroundColor: BAND_BACKGROUND[band], color: "#fff", fontWeight: 600 };
    return sx;
};

// Cor de fundo dos cartões de topo pela banda do semáforo (cinzento neutro quando não há dados).
export const conversionCardColor = (pct: number | null) => {
    const band = conversionRateBandOf(pct);
    const color = band ? BAND_BACKGROUND[band] : DASHBOARD_COLORS.neutral;
    return color;
};
